import { useState, type ReactNode } from "react";

import type { BuyerSubmissionBulkResultDto } from "@/api/buyers";
import { ConfirmModal } from "@/components/ui/PromptModals";
import {
  Button,
  OverflowMenu,
  StatusBadge,
  useToast,
  type OverflowMenuItem,
  type SubTableColumn,
} from "@/design-system/components/ui";
import {
  useArchiveSubmission,
  useBulkArchiveSubmissions,
  useBulkDeleteSubmissions,
  useDeleteSubmission,
  useRestoreSubmission,
} from "@/hooks/buyers/useSubmissionArchive";
import { useDateFormatter } from "@/utils/datetime";

/**
 * Submission archive, restore and delete on every Submissions surface (D1–D12,
 * rulings 2026-10-14): the buyer's Submissions tab, the firm-wide list and the
 * submission page carry the full set; a listing's "Buyers presented" table
 * offers Restore on its archived rows only (it is not a D3 surface).
 *
 * <p>🔴 <b>Every yes/no here is the server's.</b> A row's `canArchive`,
 * `archiveBlockedReason`, `canDelete` and `deleteBlockedReason` are computed by
 * the same predicates the endpoints run; this file renders them and never
 * re-derives a rule. The one client-side read is `Submissions.Delete`, which
 * decides whether a Delete item is offered at all (D8: absent for non-admins);
 * the HOST reads it (`canDeleteAtAll`) and the server checks it again.</p>
 *
 * <p>No browser `confirm()` anywhere (D12): archive and restore act at once with
 * an UNDO toast; delete always goes through the app's own confirm modal, naming
 * what it deletes and what it skips.</p>
 */
export const submissionArchiveCopy = {
  showArchived: "Show archived",
  archivedPill: "Archived",
  actionsHeader: "Actions",
  menuLabel: (listing: string) => `More actions for ${listing}`,
  archive: "Archive",
  restore: "Restore",
  delete: "Delete",
  undo: "Undo",
  archivedOne: (listing: string) => `Archived ${listing}.`,
  restoredOne: (listing: string) => `Restored ${listing}.`,
  deletedOne: (listing: string) => `Deleted ${listing}.`,
  bulkArchived: (n: number) => `${n} archived`,
  bulkDeleted: (n: number) => `${n} deleted`,
  skipped: (n: number) => `${n} skipped`,
  failed: (n: number) => `${n} failed`,
  skippedLine: (listing: string, reason: string) => `${listing} (${reason})`,
  alreadyArchived: "already archived",
  nothingToArchive: "Nothing to archive — every ticked submission is already archived.",
  deleteTitle: (n: number) => (n === 1 ? "Delete this submission?" : `Delete ${n} submissions?`),
  deleteLead: (n: number) =>
    n === 1
      ? "This removes the submission from every table. It can't be undone."
      : `This removes ${n} submissions from every table. It can't be undone.`,
  deleteNone: "None of the ticked submissions can be deleted.",
  willDelete: "Will be deleted",
  willSkip: "Will be skipped",
  deleteConfirm: (n: number) => (n === 1 ? "Delete" : `Delete ${n}`),
  cancel: "Cancel",
  banner: (date: string, name?: string | null) =>
    name ? `Archived ${date} by ${name}` : `Archived ${date}`,
  bannerNote:
    "Hidden from submission tables, active counts and pickers. Nothing else about it has changed, and it still counts as presented.",
  bulkRestored: (n: number) => `${n} restored`,
} as const;

/** What a row, a ticked pick or the submission page knows about one submission
 *  — the subset every archive/delete control reads. */
export interface SubmissionRemovalTarget {
  id: string;
  /** The listing's WHOLE name, for toasts and the confirm modal. */
  listingName: string;
  isArchived?: boolean;
  canArchive?: boolean;
  archiveBlockedReason?: string | null;
  canDelete?: boolean;
  deleteBlockedReason?: string | null;
}

/** "Show archived" (D4). Off by default; the host decides where the state lives
 *  — the link on the buyer tab and the firm list, local on a listing (R4). */
export function ShowArchivedToggle({
  checked,
  onChange,
  testId = "show-archived",
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  testId?: string;
}) {
  return (
    <label className="inline-flex items-center gap-space-2 text-small text-onyx">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        data-testid={testId}
      />
      {submissionArchiveCopy.showArchived}
    </label>
  );
}

/**
 * The header of a row-actions column: named for screen readers, invisible.
 *
 * <p>🔴 The `relative` wrapper is load-bearing. `sr-only` is absolutely
 * positioned, and `SubTable`'s scroll wrapper is not a positioned box, so a
 * bare `sr-only` in the LAST column escapes the wrapper's overflow clip and
 * widens the whole document (caught by `buyer-submission-drawer-overflow`).
 * Anchoring it here keeps it inside the scrolled table.</p>
 */
export function ActionsColumnHeader() {
  return (
    <span className="relative inline-block">
      <span className="sr-only">{submissionArchiveCopy.actionsHeader}</span>
    </span>
  );
}

export function ArchivedPill() {
  return (
    <StatusBadge tone="muted" className="shrink-0" data-testid="submission-archived-pill">
      {submissionArchiveCopy.archivedPill}
    </StatusBadge>
  );
}

/**
 * Archived rows appear GREYED with an "Archived" pill (D4). `SubTable` has no
 * per-row class, so every cell but the kept ones (the actions — Restore must
 * read at full strength) is dimmed, and the pill rides beside the status badge.
 */
export function withArchivedRows<T extends { isArchived?: boolean }>(
  columns: SubTableColumn<T>[],
  { pillColumnKey, keepKeys = [] }: { pillColumnKey: string; keepKeys?: readonly string[] },
): SubTableColumn<T>[] {
  return columns.map((c) => {
    if (keepKeys.includes(c.key)) return c;
    const render = c.render;
    return {
      ...c,
      render: (row: T) => {
        if (!row.isArchived) return render(row);
        return (
          <div className="opacity-60" data-testid="submission-archived-cell">
            {c.key === pillColumnKey ? (
              <span className="inline-flex flex-wrap items-center gap-space-1">
                {render(row)}
                <ArchivedPill />
              </span>
            ) : (
              render(row)
            )}
          </div>
        );
      },
    };
  });
}

/** One sentence for a bulk result: "2 archived · 1 skipped: Acme (Escalated…)". */
function bulkSentence(
  done: string,
  result: BuyerSubmissionBulkResultDto,
  extraSkipped: { name: string; reason: string }[] = [],
): string {
  const skippedItems = [
    ...result.items
      .filter((i) => i.outcome === "Skipped")
      .map((i) => ({ name: i.listingDisplayName, reason: i.reason ?? "" })),
    ...extraSkipped,
  ];
  const failedItems = result.items.filter((i) => i.outcome === "Failed");
  const parts = [done];
  if (skippedItems.length > 0)
    parts.push(
      `${submissionArchiveCopy.skipped(skippedItems.length)}: ${skippedItems
        .map((s) => submissionArchiveCopy.skippedLine(s.name, s.reason.replace(/\.$/, "")))
        .join(", ")}`,
    );
  if (failedItems.length > 0)
    parts.push(
      `${submissionArchiveCopy.failed(failedItems.length)}: ${failedItems
        .map((s) => submissionArchiveCopy.skippedLine(s.listingDisplayName, (s.reason ?? "").replace(/\.$/, "")))
        .join(", ")}`,
    );
  return parts.join(" · ");
}

const UNDO_TOAST_MS = 8000;

export interface SubmissionRemoval {
  archive: (t: SubmissionRemovalTarget) => void;
  restore: (t: SubmissionRemovalTarget) => void;
  /** Opens the confirm modal for one or more submissions (D9, D12). */
  requestDelete: (targets: SubmissionRemovalTarget[]) => void;
  /** The bulk bar's ARCHIVE (D3a): acts on the eligible, names the skipped. */
  archiveMany: (targets: SubmissionRemovalTarget[]) => void;
  /** Whether this reader may delete at all (`Submissions.Delete`, D8). */
  canDeleteAtAll: boolean;
  pending: boolean;
  /** The confirm modal — mount it once in the host. */
  node: ReactNode;
}

/**
 * The archive / restore / delete flows for a host surface.
 *
 * @param onRemoved Called with the ids that LEFT the default view (archived or
 * deleted), so a host holding a selection drops them (D4: "an archived
 * submission is dropped from any current selection").
 * @param onDeletedOne Called after a single delete — the submission page uses
 * it to leave a record that no longer exists.
 */
export function useSubmissionRemoval({
  onRemoved,
  onDeletedOne,
  canDeleteAtAll = false,
}: {
  onRemoved?: (ids: string[]) => void;
  onDeletedOne?: (id: string) => void;
  /** The host's `hasPermission("Submissions.Delete")` (D8). Absent offers no
   *  Delete anywhere this hook drives. */
  canDeleteAtAll?: boolean;
} = {}): SubmissionRemoval {
  const toast = useToast();
  const archiveOne = useArchiveSubmission();
  const restoreOne = useRestoreSubmission();
  const deleteOne = useDeleteSubmission();
  const bulkArchive = useBulkArchiveSubmissions();
  const bulkDelete = useBulkDeleteSubmissions();
  const [deleting, setDeleting] = useState<SubmissionRemovalTarget[] | null>(null);

  const copy = submissionArchiveCopy;

  function restore(t: SubmissionRemovalTarget) {
    restoreOne.mutate(t.id, {
      onSuccess: () =>
        toast.success(copy.restoredOne(t.listingName), {
          durationMs: UNDO_TOAST_MS,
          action: { label: copy.undo, onClick: () => archive(t) },
        }),
      onError: (e) => toast.error(e.message),
    });
  }

  function archive(t: SubmissionRemovalTarget) {
    archiveOne.mutate(t.id, {
      onSuccess: () => {
        onRemoved?.([t.id]);
        toast.success(copy.archivedOne(t.listingName), {
          durationMs: UNDO_TOAST_MS,
          action: { label: copy.undo, onClick: () => restore(t) },
        });
      },
      onError: (e) => toast.error(e.message),
    });
  }

  function archiveMany(targets: SubmissionRemovalTarget[]) {
    const already = targets.filter((t) => t.isArchived);
    const toSend = targets.filter((t) => !t.isArchived);
    if (toSend.length === 0) {
      toast.info(copy.nothingToArchive);
      return;
    }
    bulkArchive.mutate(
      { ids: toSend.map((t) => t.id), archive: true },
      {
        onSuccess: (result) => {
          const done = result.items.filter((i) => i.outcome === "Succeeded").map((i) => i.submissionId);
          onRemoved?.(done);
          const sentence = bulkSentence(
            copy.bulkArchived(result.succeeded),
            result,
            already.map((t) => ({ name: t.listingName, reason: copy.alreadyArchived })),
          );
          const tone = result.succeeded > 0 ? toast.success : toast.info;
          tone(sentence, {
            durationMs: UNDO_TOAST_MS,
            action:
              done.length > 0
                ? {
                    label: copy.undo,
                    onClick: () =>
                      bulkArchive.mutate(
                        { ids: done, archive: false },
                        {
                          onSuccess: (r) => toast.success(copy.bulkRestored(r.succeeded)),
                          onError: (e) => toast.error(e.message),
                        },
                      ),
                  }
                : undefined,
          });
        },
        onError: (e) => toast.error(e.message),
      },
    );
  }

  const eligible = deleting?.filter((t) => t.canDelete) ?? [];
  const skipped = deleting?.filter((t) => !t.canDelete) ?? [];

  function confirmDelete() {
    const targets = eligible;
    setDeleting(null);
    if (targets.length === 0) return;
    if (targets.length === 1 && skipped.length === 0) {
      const t = targets[0];
      deleteOne.mutate(t.id, {
        onSuccess: () => {
          onRemoved?.([t.id]);
          toast.success(copy.deletedOne(t.listingName));
          onDeletedOne?.(t.id);
        },
        onError: (e) => toast.error(e.message),
      });
      return;
    }
    bulkDelete.mutate(
      targets.map((t) => t.id),
      {
        onSuccess: (result) => {
          onRemoved?.(result.items.filter((i) => i.outcome === "Succeeded").map((i) => i.submissionId));
          const sentence = bulkSentence(
            copy.bulkDeleted(result.succeeded),
            result,
            skipped.map((t) => ({ name: t.listingName, reason: t.deleteBlockedReason ?? "" })),
          );
          (result.succeeded > 0 ? toast.success : toast.info)(sentence);
        },
        onError: (e) => toast.error(e.message),
      },
    );
  }

  const node = deleting ? (
    <ConfirmModal
      open
      title={copy.deleteTitle(Math.max(1, eligible.length))}
      destructive
      confirmLabel={copy.deleteConfirm(eligible.length)}
      cancelLabel={copy.cancel}
      onCancel={() => setDeleting(null)}
      onConfirm={confirmDelete}
      message={
        <div className="grid gap-space-3" data-testid="submission-delete-confirm">
          <p className="m-0">{eligible.length > 0 ? copy.deleteLead(eligible.length) : copy.deleteNone}</p>
          {eligible.length > 0 && (
            <div>
              <p className="m-0 font-medium text-onyx">{copy.willDelete}</p>
              <ul className="m-0 pl-space-4" data-testid="submission-delete-eligible">
                {eligible.map((t) => (
                  <li key={t.id}>{t.listingName}</li>
                ))}
              </ul>
            </div>
          )}
          {skipped.length > 0 && (
            <div>
              <p className="m-0 font-medium text-onyx">{copy.willSkip}</p>
              <ul className="m-0 pl-space-4" data-testid="submission-delete-skipped">
                {skipped.map((t) => (
                  <li key={t.id}>
                    {copy.skippedLine(t.listingName, t.deleteBlockedReason ?? "")}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      }
    />
  ) : null;

  return {
    archive,
    restore,
    requestDelete: (targets) => setDeleting(targets),
    archiveMany,
    canDeleteAtAll,
    pending:
      archiveOne.isPending || restoreOne.isPending || deleteOne.isPending
      || bulkArchive.isPending || bulkDelete.isPending,
    node,
  };
}

/** The ••• items for one submission: Archive or Restore, and Delete for an admin
 *  (D3, D8). Empty for a reader who may do none of them. */
export function submissionMenuItems(
  t: SubmissionRemovalTarget,
  removal: SubmissionRemoval,
): OverflowMenuItem[] {
  const copy = submissionArchiveCopy;
  const items: OverflowMenuItem[] = [];
  if (t.isArchived) {
    if (t.canArchive) items.push({ label: copy.restore, onSelect: () => removal.restore(t) });
  } else if (t.canArchive || t.archiveBlockedReason) {
    items.push({
      label: copy.archive,
      onSelect: () => removal.archive(t),
      disabled: !t.canArchive,
      hint: t.canArchive ? undefined : (t.archiveBlockedReason ?? undefined),
    });
  }
  if (removal.canDeleteAtAll && (t.canDelete || t.deleteBlockedReason)) {
    items.push({
      label: copy.delete,
      destructive: true,
      onSelect: () => removal.requestDelete([t]),
      disabled: !t.canDelete,
      hint: t.canDelete ? undefined : (t.deleteBlockedReason ?? undefined),
    });
  }
  return items;
}

export function SubmissionRowMenu({
  target,
  removal,
  testId = "submission-row-actions",
}: {
  target: SubmissionRemovalTarget;
  removal: SubmissionRemoval;
  testId?: string;
}) {
  const items = submissionMenuItems(target, removal);
  if (items.length === 0) return null;
  return (
    <OverflowMenu
      items={items}
      label={submissionArchiveCopy.menuLabel(target.listingName)}
      data-testid={testId}
    />
  );
}

/**
 * D5 — an archived submission's page still opens, and says so: "Archived {date}
 * by {name}" with RESTORE. Nothing else on the page changes.
 */
export function SubmissionArchivedBanner({
  target,
  archivedAtUtc,
  archivedByName,
  removal,
}: {
  target: SubmissionRemovalTarget;
  archivedAtUtc?: string | null;
  archivedByName?: string | null;
  removal: SubmissionRemoval;
}) {
  const { date } = useDateFormatter();
  if (!target.isArchived) return null;
  return (
    <div
      className="mb-space-4 rounded-md border border-status-warning bg-white-warm p-space-3"
      data-testid="submission-archived-banner"
    >
      <div className="flex flex-wrap items-start justify-between gap-space-3">
        <div className="grid min-w-0 gap-space-1">
          <p className="m-0 text-small font-medium text-onyx">
            {submissionArchiveCopy.banner(archivedAtUtc ? date(archivedAtUtc) : "", archivedByName)}
          </p>
          <p className="m-0 text-caption text-gray-mid">{submissionArchiveCopy.bannerNote}</p>
        </div>
        {target.canArchive && (
          <Button
            size="sm"
            variant="secondary"
            disabled={removal.pending}
            onClick={() => removal.restore(target)}
            data-testid="submission-restore"
          >
            {submissionArchiveCopy.restore}
          </Button>
        )}
      </div>
    </div>
  );
}
